import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Trash2, Loader2, Users, Zap, CalendarPlus, CheckCircle2, XCircle, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useConfirmDialog } from '@/components/useConfirmDialog';
import { ScrollArea } from '@/components/ui/scroll-area';

const initialForm = { nome: '', descricao: '', turma_id: '', data: '', horario: '', horario_fim: '', local: '' };
const diasSemana = ['Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado', 'Domingo'];
const gerarInitial = { nome: '', turma_id: '', dia_semana: '0', horario: '', horario_fim: '', local: '', descricao: '', data_inicial: '' };

export default function TreinosPage() {
  const { user } = useAuth();
  const [treinos, setTreinos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [confirmadosDialog, setConfirmadosDialog] = useState(null);
  const [confirmados, setConfirmados] = useState([]);
  const [loadingConfirmados, setLoadingConfirmados] = useState(false);
  const [gerarDialog, setGerarDialog] = useState(false);
  const [gerarForm, setGerarForm] = useState(gerarInitial);
  const [gerarSaving, setGerarSaving] = useState(false);
  const [gerarResult, setGerarResult] = useState(null);
  const [professores, setProfessores] = useState([]);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  useEffect(() => {
    const fetches = [api.get('/gestor/treinos'), api.get('/gestor/turmas')];
    if (['gestor', 'socio'].includes(user?.role)) fetches.push(api.get('/gestor/colaboradores'));
    Promise.all(fetches)
      .then(([t, tu, col]) => {
        setTreinos(t.data);
        setTurmas(tu.data);
        if (col) {
          const profs = (col.data || []).filter(c => c.role === 'professor' || c.tipo_colaborador === 'professor');
          setProfessores(profs);
        }
      })
      .catch(() => toast.error('Erro ao carregar')).finally(() => setLoading(false));
  }, [user?.role]);

  const refresh = async () => { const r = await api.get('/gestor/treinos'); setTreinos(r.data); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await api.put(`/gestor/treinos/${editing.id}`, form); toast.success('Treino atualizado'); }
      else { await api.post('/gestor/treinos', form); toast.success('Treino criado'); }
      setDialogOpen(false); setEditing(null); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!await confirm('Remover Treino', 'Tem certeza que deseja remover este treino?')) return;
    try { await api.delete(`/gestor/treinos/${id}`); toast.success('Treino removido'); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const openCreate = () => { setEditing(null); setForm(initialForm); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ nome: t.nome, descricao: t.descricao || '', turma_id: t.turma_id, data: t.data?.split('T')[0] || t.data || '', horario: t.horario || '', horario_fim: t.horario_fim || '', local: t.local || '' }); setDialogOpen(true); };

  const getTurmaName = (id) => turmas.find(t => t.id === id)?.nome || '-';

  const handleGerarAuto = async (e) => {
    e.preventDefault();
    setGerarSaving(true);
    try {
      const payload = { ...gerarForm, dia_semana: parseInt(gerarForm.dia_semana) };
      const res = await api.post('/gestor/treinos/gerar-automatico', payload);
      setGerarResult(res.data);
      toast.success(res.data.message);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setGerarSaving(false);
  };

  const openConfirmados = async (treino) => {
    setConfirmadosDialog(treino);
    setLoadingConfirmados(true);
    try {
      const res = await api.get(`/gestor/treinos/${treino.id}/confirmados`);
      setConfirmados(res.data.confirmados || []);
    } catch { toast.error('Erro ao carregar confirmados'); }
    setLoadingConfirmados(false);
  };

  const getTreinoDateTime = (treino) => {
    const data = (treino.data || '').split('T')[0];
    const horario = (treino.horario || '00:00').slice(0, 5);
    const dataHora = new Date(`${data}T${horario}:00`);

    return Number.isNaN(dataHora.getTime()) ? null : dataHora;
  };

  const agora = new Date();

  const treinosOrdenados = [...treinos].sort((a, b) => {
    const dataA = getTreinoDateTime(a);
    const dataB = getTreinoDateTime(b);

    if (!dataA) return 1;
    if (!dataB) return -1;

    return dataA - dataB;
  });

  const proximosTreinos = treinosOrdenados.filter((treino) => {
    const dataHora = getTreinoDateTime(treino);
    return dataHora && dataHora >= agora;
  });

  const treinosAnteriores = treinosOrdenados
    .filter((treino) => {
      const dataHora = getTreinoDateTime(treino);
      return dataHora && dataHora < agora;
    })
    .reverse();

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="treinos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">TREINOS</h1>
        <div className="flex gap-2">
          {['gestor', 'socio'].includes(user?.role) && (
            <Button onClick={() => { setGerarForm(gerarInitial); setGerarResult(null); setGerarDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="gerar-auto-button">
              <CalendarPlus className="w-4 h-4 mr-2" /> Gerar Automaticamente
            </Button>
          )}
          <Button onClick={openCreate} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="add-treino-button"><Plus className="w-4 h-4 mr-2" /> Novo Treino</Button>
        </div>
      </div>

      {loading ? (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="py-12 text-center text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
            Carregando treinos...
          </CardContent>
        </Card>
      ) : treinos.length === 0 ? (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="py-12 text-center text-zinc-500">
            Nenhum treino cadastrado
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Próximos treinos</h2>
              <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                {proximosTreinos.length}
              </Badge>
            </div>

            {proximosTreinos.length === 0 ? (
              <Card className="bg-[#141414] border-[#27272A]">
                <CardContent className="py-8 text-center text-zinc-500">
                  Nenhum próximo treino agendado
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {proximosTreinos.map((t, index) => (
                  <Card
                    key={t.id}
                    className={`bg-[#141414] border-[#27272A] overflow-hidden ${
                      index === 0 ? 'ring-2 ring-blue-500 border-blue-500' : ''
                    }`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          {index === 0 && (
                            <Badge className="mb-3 bg-blue-500 text-white border-blue-500">
                              Próximo treino
                            </Badge>
                          )}

                          <h3 className="text-2xl font-black font-['Barlow_Condensed'] uppercase text-white truncate">
                            {t.nome}
                          </h3>

                          <p className="text-zinc-400 mt-1">
                            {getTurmaName(t.turma_id)}
                          </p>
                        </div>

                        <div className="text-right shrink-0">
                          <p className="text-lg font-bold text-white">
                            {new Date(`${(t.data || '').split('T')[0]}T12:00:00`).toLocaleDateString('pt-BR')}
                          </p>
                          <p className="text-2xl font-black text-blue-400">
                            {(t.horario || '').slice(0, 5)}
                            {t.horario_fim ? ` às ${t.horario_fim.slice(0, 5)}` : ''}
                          </p>
                          {t.local && (
                            <p className="mt-1 text-sm text-zinc-400 flex items-center justify-end gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {t.local}
                            </p>
                          )}
                        </div>
                      </div>

                      {t.descricao && (
                        <p className="mt-4 text-sm text-zinc-400 border-t border-[#27272A] pt-4">
                          {t.descricao}
                        </p>
                      )}

                      <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-[#27272A]">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openConfirmados(t)}
                          className="text-blue-400 hover:text-blue-300 gap-2"
                          data-testid={`confirmados-btn-${t.id}`}
                        >
                          <Users className="w-4 h-4" />
                          Ver confirmados
                        </Button>

                        {['gestor', 'socio'].includes(user?.role) && (
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEdit(t)}
                              className="text-zinc-400 hover:text-white"
                              title="Editar treino"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(t.id)}
                              className="text-zinc-400 hover:text-red-400"
                              title="Excluir treino"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>

          {treinosAnteriores.length > 0 && (
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-zinc-300">Treinos anteriores</h2>
                <Badge className="bg-zinc-500/10 text-zinc-400 border-zinc-500/20">
                  {treinosAnteriores.length}
                </Badge>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {treinosAnteriores.map((t) => (
                  <Card key={t.id} className="bg-[#101010] border-[#27272A] opacity-80">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <h3 className="text-xl font-bold text-zinc-300 truncate">
                            {t.nome}
                          </h3>
                          <p className="text-sm text-zinc-500 mt-1">
                            {getTurmaName(t.turma_id)}
                          </p>
                        </div>

                        <div className="text-right shrink-0">
                          <p className="text-sm font-medium text-zinc-400">
                            {new Date(`${(t.data || '').split('T')[0]}T12:00:00`).toLocaleDateString('pt-BR')}
                          </p>
                          <p className="text-lg font-bold text-zinc-500">
                            {(t.horario || '').slice(0, 5)}
                            {t.horario_fim ? ` às ${t.horario_fim.slice(0, 5)}` : ''}
                          </p>
                          {t.local && (
                            <p className="mt-1 text-xs text-zinc-500 flex items-center justify-end gap-1">
                              <MapPin className="w-3 h-3" />
                              {t.local}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#27272A]">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openConfirmados(t)}
                          className="text-zinc-400 hover:text-white gap-2"
                        >
                          <Users className="w-4 h-4" />
                          Confirmados
                        </Button>

                        {['gestor', 'socio'].includes(user?.role) && (
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEdit(t)}
                              className="text-zinc-500 hover:text-white"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(t.id)}
                              className="text-zinc-500 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">{editing ? 'Editar Treino' : 'Novo Treino'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome</Label><Input value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="treino-form-nome" /></div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Turma</Label>
              <Select value={form.turma_id} onValueChange={v => setForm({...form, turma_id: v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]"><SelectValue placeholder="Selecionar turma" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#27272A]">
                  {turmas.map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Data</Label><Input type="date" value={form.data} onChange={e => setForm({...form, data: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Horario Inicio</Label><Input type="time" value={form.horario} onChange={e => setForm({...form, horario: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required /></div>
              <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Horario Fim</Label><Input type="time" value={form.horario_fim} onChange={e => setForm({...form, horario_fim: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" /></div>
            </div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Local do treino</Label><Input value={form.local} onChange={e => setForm({...form, local: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" placeholder="Ex: CMT III" data-testid="treino-form-local" /></div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Descricao</Label><Textarea value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-zinc-400">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="treino-form-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Salvar' : 'Criar')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmadosDialog} onOpenChange={() => setConfirmadosDialog(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">
              Confirmados - {confirmadosDialog?.nome}
            </DialogTitle>
            <p className="text-xs text-zinc-500">{confirmadosDialog?.data} - {confirmadosDialog?.horario}</p>
          </DialogHeader>
          <div className="max-h-[300px] overflow-y-auto">
            {loadingConfirmados ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
            ) : confirmados.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-8">Nenhum aluno confirmou ainda</p>
            ) : (
              <div className="space-y-2">
                {confirmados.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-2 px-3 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-white">{c.aluno_nome}</span>
                    </div>
                    <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">Eu vou</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-zinc-600 mt-2">{confirmados.length} aluno(s) confirmado(s)</p>
        </DialogContent>
      </Dialog>
      <Dialog open={gerarDialog} onOpenChange={(v) => { if (!gerarSaving) setGerarDialog(v); }}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-lg" data-testid="gerar-auto-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-emerald-400" />
              Gerar Treinos Automaticamente
            </DialogTitle>
            <p className="text-xs text-zinc-500 mt-1">Cria treinos para o restante do mes com base no dia da semana escolhido</p>
          </DialogHeader>

          {!gerarResult ? (
            <form onSubmit={handleGerarAuto} className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome base do treino *</Label>
                <Input value={gerarForm.nome} onChange={e => setGerarForm({...gerarForm, nome: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" placeholder="Ex: Muay Thai - Turma A" required data-testid="gerar-nome" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Turma *</Label>
                <Select value={gerarForm.turma_id} onValueChange={v => setGerarForm({...gerarForm, turma_id: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="gerar-turma"><SelectValue placeholder="Selecionar turma" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#27272A]">
                    {turmas.filter(t => t.ativo !== false).map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {professores.length > 0 && (
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Professor</Label>
                  <Select value={gerarForm.professor_id || ''} onValueChange={v => setGerarForm({...gerarForm, professor_id: v})}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="gerar-professor"><SelectValue placeholder="Selecionar professor (opcional)" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#27272A]">
                      {professores.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Dia da Semana *</Label>
                <Select value={gerarForm.dia_semana} onValueChange={v => setGerarForm({...gerarForm, dia_semana: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="gerar-dia-semana"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#27272A]">
                    {diasSemana.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Horario Inicio *</Label>
                  <Input type="time" value={gerarForm.horario} onChange={e => setGerarForm({...gerarForm, horario: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="gerar-horario" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Horario Fim</Label>
                  <Input type="time" value={gerarForm.horario_fim} onChange={e => setGerarForm({...gerarForm, horario_fim: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" data-testid="gerar-horario-fim" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Local do treino</Label>
                <Input value={gerarForm.local} onChange={e => setGerarForm({...gerarForm, local: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" placeholder="Ex: CMT III" data-testid="gerar-local" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data Inicial</Label>
                <Input type="date" value={gerarForm.data_inicial} onChange={e => setGerarForm({...gerarForm, data_inicial: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" data-testid="gerar-data-inicial" />
                <p className="text-[10px] text-zinc-600 mt-1">Se vazio, usa a data de hoje</p>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descricao</Label>
                <Textarea value={gerarForm.descricao} onChange={e => setGerarForm({...gerarForm, descricao: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" placeholder="Descricao opcional" rows={2} />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setGerarDialog(false)} className="text-zinc-400">Cancelar</Button>
                <Button type="submit" disabled={gerarSaving || !gerarForm.turma_id || !gerarForm.nome || !gerarForm.horario} className="bg-emerald-600 hover:bg-emerald-700" data-testid="gerar-submit">
                  {gerarSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Gerar Treinos'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4" data-testid="gerar-result">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-emerald-400">{gerarResult.created} treino(s) criado(s)</p>
                  {gerarResult.skipped > 0 && <p className="text-xs text-zinc-400">{gerarResult.skipped} ignorado(s) por duplicidade</p>}
                </div>
              </div>
              {gerarResult.created_dates?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2 font-medium">Datas criadas</p>
                  <ScrollArea className="max-h-[140px]">
                    <div className="space-y-1">
                      {gerarResult.created_dates.map(d => (
                        <div key={d} className="flex items-center gap-2 py-1 px-2 rounded bg-[#0A0A0A] border border-[#27272A]">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                          <span className="text-sm text-white">{d} ({diasSemana[new Date(d + 'T12:00:00').getDay() === 0 ? 6 : new Date(d + 'T12:00:00').getDay() - 1]})</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
              {gerarResult.skipped_dates?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2 font-medium">Datas ignoradas (duplicidade)</p>
                  <ScrollArea className="max-h-[100px]">
                    <div className="space-y-1">
                      {gerarResult.skipped_dates.map(d => (
                        <div key={d} className="flex items-center gap-2 py-1 px-2 rounded bg-[#0A0A0A] border border-orange-500/20">
                          <XCircle className="w-3.5 h-3.5 text-orange-400" />
                          <span className="text-sm text-zinc-400">{d}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => { setGerarDialog(false); setGerarResult(null); }} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="gerar-fechar">Fechar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
}